import { theme as rimbleTheme } from 'rimble-ui'

export enum Color {
  primary = '#0078EF',
  primaryDark = '#0966c1',
  primaryLight = '#ddeeff',
  primaryLighterVariant = '#489CF0',
  primaryLighterVariant2 = '#e6f2fe',
  primaryAlpha03 = 'rgba(1, 121, 239, 0.03)',
  successLight2 = '#6FCF97',
  success = '#27AE60',
  successDark = '#219653',
  successLight = '#E8FAF0',
  warning = '#F2994A',
  warningDark = '#D46400',
  warningLight = '#FFF7DF',
  pending = '#F2C94C',
  danger = '#EB5757',
  dangerDark = '#E40101',
  dangerLight = '#FFECEC',
  black = '#262626',
  nearBlack = '#4d4d4d',
  grey = '#9FA3BC',
  greyDark = '#314F72',
  greyLight = '#D3D5E5',
  background = '#F0F3F7',
  greyBackground = '#EFF2F5',
  greyBackgroundHover = '#d6dadf',
  textRare = '#009FD2',
  border = '#E9EAF2',
  offWhite = '#F0F4FA',
  orangeLight = '#FDEADA',
  orangeDark = '#8F4C11',
}

export enum RewardsColor {
  Base = '#E9EAF5',
  Silver = '#E4F1F7',
  BaseDark = '#9FA3BC',
  SilverDark = '#8DB3C4',
  Gold = '#EBD08D',
  Platinum = '#A1E4E6',
}

const theme = {
  ...rimbleTheme,
  fontWeights: [0, 300, 400, 500, 600, 700],
  lineHeights: {
    solid: 1,
    title: 1.25,
    copy: 1.5,
    button: 3,
  },
  colors: {
    ...rimbleTheme.colors,
    primary: Color.primary,
    'primary-dark': Color.primaryDark,
    'primary-light': Color.primaryLight,
    'primary-lighter-variant': Color.primaryLighterVariant,
    'primary-lighter-variant2': Color.primaryLighterVariant2,
    success: Color.success,
    'success-dark': Color.successDark,
    'success-light': Color.successLight,
    warning: Color.warning,
    'warning-dark': Color.warningDark,
    'warning-light': Color.warningLight,
    danger: Color.danger,
    'danger-dark': Color.dangerDark,
    'danger-light': Color.dangerLight,
    black: Color.black,
    'near-black': Color.nearBlack,
    grey: Color.grey,
    'light-gray': Color.greyLight,
    'dark-gray': Color.greyDark,
    background: Color.background,
    greyBackground: Color.greyBackground,
    greyBackgroundHover: Color.greyBackgroundHover,
    text: Color.black,
    'text-light': Color.nearBlack,
    'navy-dark': Color.greyDark,
    'text-rare': Color.textRare,
    'primary-alpha-03': Color.primaryAlpha03,
    border: Color.border,
    'off-white': Color.offWhite,
    'orange-light': Color.orangeLight,
    'orange-dark': Color.orangeDark,
    pending: Color.pending,
    sidebar: {
      background: Color.greyDark,
    },
    icon: {
      primary: Color.primary,
    },
  },
  buttonSizes: {
    ...rimbleTheme.buttonSizes,
    medium: {
      fontSize: '1rem',
      height: '2.5rem',
    },
  },
  buttons: {
    ...rimbleTheme.buttons,
    plain: {
      padding: 0,
      height: 'fit-content',
      minWidth: 'fit-content',
      boxShadow: 'none',
      '--main-color': 'transparent',
      '--contrast-color': Color.grey,
      '&:hover': {
        color: Color.primary,
        boxShadow: 'none !important',
        background: 'transparent !important',
        '--main-color': 'transparent',
        '--contrast-color': Color.primary,
      },
      '&:active': {
        color: Color.primary,
        boxShadow: 'none !important',
        background: 'transparent !important',
        '--main-color': 'transparent',
        '--contrast-color': Color.primary,
      },
    },
  },
  messageStyle: {
    ...rimbleTheme.messageStyle,
    success: {
      color: Color.successDark,
      backgroundColor: Color.successLight,
      borderColor: Color.successDark,
    },
    warning: {
      color: Color.warningDark,
      backgroundColor: Color.warningLight,
      borderColor: Color.warningDark,
    },
    danger: {
      color: Color.dangerDark,
      backgroundColor: Color.dangerLight,
      borderColor: Color.dangerDark,
    },
  },
  shadows: [
    ...rimbleTheme.shadows,
    '0px 2px 2px rgba(152, 162, 179, 0.15), 0px 12px 24px rgba(152, 162, 179, 0.15)',
  ],
  zIndices: {
    layerZero: 0,
    layerOne: 1,
    layerTwo: 2,
    layerThree: 3,
    header: 10,
    tokenInfoProgressBar: 10,
    alert: 50,
    dialog: 50,
    navigationMenu: 99,
    threeDotsMenu: 999,
    dropDownButtonMenu: 999,
    yotiModal: 9999,
    commonDialog: 99999,
    footer: 9999,
    navigation: 9999,
    datePicker: 99999,
    drawer: 99999,
    cookieBanner: 99999,
    snackbar: 999999,
    fireworks: 9999999,
    tooltip: 99999999,
  },
  palette: {
    primary: {
      main: Color.primary,
      light: Color.primaryLight,
      dark: Color.primaryDark,
      contrastText: '#fff',
    },
    text: {
      primary: Color.primary,
      secondary: Color.grey,
      disabled: Color.greyLight,
      hint: Color.greyLight,
    },
  },
  layout: {
    navigationBar: {
      heightPixels: 56,
      desktopWidthPixels: 304,
    },
    announcements: {
      heightPixels: 40,
      mobileHeightPixels: 120,
    },
    footer: {
      heightPixels: 56,
    },
  },
}

export type Theme = typeof theme

export default theme
